import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGavel, faEuroSign, faTrophy, faClock, faArrowLeft,
  faCalendarAlt, faInfoCircle, faExclamationTriangle, faCheckCircle,
  faHistory, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { initializeSocket, joinAuction, leaveAuction, onAuctionUpdate, offAuctionUpdate } from '../../services/socketService';

interface Auction {
  _id: string;
  auctionNumber: string;
  title: string;
  description?: string;
  masterArticleId: {
    _id: string;
    masterArticleNumber: string;
    name: string;
  };
  masterArticleNumber: string;
  masterArticleName: string;
  validFrom: string;
  validUntil: string;
  calendarWeek: number;
  year: number;
  biddingStartTime: string;
  biddingEndTime: string;
  minimumBidIncrement: number;
  startingPrice?: number;
  reservePrice?: number;
  estimatedQuantity?: number;
  unit: string;
  currentLowestBid?: number;
  totalBidsCount: number;
  status: string;
}

interface Bid {
  _id: string;
  bidAmount: number;
  submittedAt: string;
  status: string;
  isCurrentLowest: boolean;
  comment?: string;
  deliveryTerms?: string;
  paymentTerms?: string;
  modificationCount: number;
}

const SupplierAuctionDetail: React.FC = () => {
  const { auctionId, masterToken } = useParams<{ auctionId: string; masterToken: string }>();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [myBids, setMyBids] = useState<Bid[]>([]);
  const [myCurrentBid, setMyCurrentBid] = useState<Bid | null>(null);
  const [isMyBidLowest, setIsMyBidLowest] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Bid Form
  const [bidAmount, setBidAmount] = useState('');
  const [comment, setComment] = useState('');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Toast State
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    loadAuctionDetails();
    
    if (!auctionId) return;

    // Initialize Socket.IO
    initializeSocket();
    joinAuction(auctionId);

    // Listen for real-time updates
    const handleAuctionUpdate = (data: any) => {
      console.log('📢 Received auction-updated event:', data);
      loadAuctionDetails();
    };

    onAuctionUpdate(handleAuctionUpdate);

    return () => {
      offAuctionUpdate(handleAuctionUpdate);
      leaveAuction(auctionId);
    };
  }, [auctionId]);

  const loadAuctionDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/supplier-portal/auctions/${auctionId}`);
      
      setAuction(response.data.auction);
      setMyBids(response.data.myBids || []);
      setMyCurrentBid(response.data.myCurrentBid || null);
      setIsMyBidLowest(response.data.isMyBidLowest || false);
      setParticipantCount(response.data.participantCount || 0);
      
      // Pre-fill bid amount slightly below current lowest
      if (response.data.auction.currentLowestBid && !bidAmount) {
        const suggested = response.data.auction.currentLowestBid - response.data.auction.minimumBidIncrement;
        setBidAmount(suggested.toFixed(2));
      }
    } catch (error: any) {
      console.error('Error loading auction details:', error);
      setError(error.response?.data?.message || 'Fehler beim Laden der Auktion');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitBid = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Konvertiere Komma zu Punkt für Parsing
    const amount = parseFloat(bidAmount.replace(',', '.'));
    
    if (isNaN(amount) || amount <= 0) {
      showToast('Bitte geben Sie einen gültigen Betrag ein', 'error');
      return;
    }

    if (auction?.currentLowestBid && amount >= auction.currentLowestBid) {
      showToast(`Ihr Gebot muss niedriger sein als € ${auction.currentLowestBid.toFixed(2).replace('.', ',')}`, 'error');
      return;
    }

    if (auction?.reservePrice && amount < auction.reservePrice) {
      showToast(`Ihr Gebot muss mindestens € ${auction.reservePrice.toFixed(2).replace('.', ',')} betragen`, 'error');
      return;
    }

    try {
      setSubmitting(true);
      await api.post(`/supplier-portal/auctions/${auctionId}/bid`, {
        bidAmount: amount
      });

      showToast(myCurrentBid ? 'Gebot erfolgreich aktualisiert!' : 'Gebot erfolgreich abgegeben!', 'success');
      
      // Reload auction details
      await loadAuctionDetails();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Fehler beim Abgeben des Gebots', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawBid = async () => {
    if (!window.confirm('Möchten Sie Ihr Gebot wirklich zurückziehen?')) return;

    const reason = window.prompt('Grund für Rückzug (optional):');

    try {
      await api.delete(`/supplier-portal/auctions/${auctionId}/bid`, {
        data: { reason }
      });

      showToast('Gebot erfolgreich zurückgezogen', 'success');
      await loadAuctionDetails();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Fehler beim Zurückziehen des Gebots', 'error');
    }
  };

  const isAuctionActive = () => {
    if (!auction) return false;
    const now = new Date();
    const start = new Date(auction.biddingStartTime);
    const end = new Date(auction.biddingEndTime);
    return auction.status === 'active' && now >= start && now <= end;
  };

  const getTimeRemaining = () => {
    if (!auction) return '';
    const now = new Date();
    const end = new Date(auction.biddingEndTime);
    const diff = end.getTime() - now.getTime();
    
    if (diff <= 0) return 'Beendet';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} Tag(e) ${hours % 24}h verbleibend`;
    }
    
    return `${hours}h ${minutes}min verbleibend`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800">Auktion nicht gefunden</p>
          <button
            onClick={() => navigate(`/supplier/portal/${masterToken}/auctions`)}
            className="mt-4 text-orange-500 hover:underline"
          >
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/supplier/portal/${masterToken}/auctions`)}
        className="mb-4 text-orange-500 hover:text-orange-600 flex items-center gap-2 font-medium"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Zurück zur Übersicht
      </button>

      {/* Header - Kompakt */}
      <div className="bg-white rounded-lg shadow-md p-5 mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{auction.title}</h1>
            <p className="text-sm text-gray-600">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
              KW {auction.calendarWeek}/{auction.year} • {auction.auctionNumber}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Artikel: {auction.masterArticleNumber} - {auction.masterArticleName}
            </p>
          </div>
          
          {isAuctionActive() && (
            <div className="text-right bg-orange-50 px-4 py-2 rounded-lg">
              <div className="text-xs text-gray-600 font-medium">Verbleibende Zeit</div>
              <div className="text-lg font-bold text-orange-600">
                <FontAwesomeIcon icon={faClock} className="mr-1" />
                {getTimeRemaining()}
              </div>
            </div>
          )}
        </div>

        {auction.description && (
          <p className="text-sm text-gray-700 mt-3 p-3 bg-gray-50 rounded">{auction.description}</p>
        )}

        {/* Auction Details Grid - Kompakt */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs text-gray-500 font-medium">Gebotsstart</div>
            <div className="text-xs font-semibold text-gray-900">
              {new Date(auction.biddingStartTime).toLocaleString('de-DE')}
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs text-gray-500 font-medium">Gebotsende</div>
            <div className="text-xs font-semibold text-gray-900">
              {new Date(auction.biddingEndTime).toLocaleString('de-DE')}
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs text-gray-500 font-medium">Lieferperiode</div>
            <div className="text-xs font-semibold text-gray-900">
              {new Date(auction.validFrom).toLocaleDateString('de-DE')} - {new Date(auction.validUntil).toLocaleDateString('de-DE')}
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <div className="text-xs text-gray-500 font-medium">Teilnehmer</div>
            <div className="text-xs font-semibold text-gray-900">{participantCount} Lieferant(en)</div>
          </div>
        </div>
      </div>

      {/* Current Lowest Bid - Großer zentraler Live-Kreis */}
      {auction.currentLowestBid !== undefined && auction.currentLowestBid !== null && (
        <div className="bg-white rounded-lg shadow-md p-8 mb-4">
          {isMyBidLowest && (
            <div className="text-center mb-4">
              <div className="text-lg font-bold text-yellow-700">
                <FontAwesomeIcon icon={faTrophy} className="mr-2 text-2xl" />
                Sie führen mit dem niedrigsten Gebot!
              </div>
            </div>
          )}
          <div className="flex justify-center">
            <div className="relative">
              {/* Äußerer pulsierender Ring */}
              <div className={`absolute inset-0 rounded-full animate-ping opacity-30 ${
                isMyBidLowest ? 'bg-yellow-400' : 'bg-green-400'
              }`}></div>
              {/* Zweiter pulsierender Ring für mehr Tiefe */}
              <div className={`absolute inset-4 rounded-full animate-ping opacity-20 ${
                isMyBidLowest ? 'bg-yellow-300' : 'bg-green-300'
              }`} style={{animationDelay: '0.3s'}}></div>
              {/* Hauptkreis */}
              <div className={`relative w-64 h-64 rounded-full shadow-2xl flex flex-col items-center justify-center text-white transform hover:scale-110 transition-all duration-500 cursor-pointer ${
                isMyBidLowest 
                  ? 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600' 
                  : 'bg-gradient-to-br from-green-400 via-green-500 to-green-600'
              }`}>
                <div className="text-base uppercase tracking-widest font-bold opacity-90 mb-2">
                  {isMyBidLowest ? 'Ihr Gebot' : 'Aktuell Niedrigstes'}
                </div>
                <div className="text-6xl font-black mb-2 animate-pulse">
                  € {auction.currentLowestBid.toFixed(2).replace('.', ',')}
                </div>
                <div className="text-lg mt-1 opacity-90 font-semibold">pro {auction.unit}</div>
                <div className="absolute bottom-6 text-xs opacity-75 uppercase tracking-wider">
                  <FontAwesomeIcon icon={faClock} className="mr-1 animate-spin" style={{animationDuration: '3s'}} />
                  Live
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Current Bid - Kompakte Info-Box wenn überboten */}
      {myCurrentBid && !isMyBidLowest && (
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm text-blue-900 font-medium">Ihr aktuelles Gebot:</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">
              € {myCurrentBid.bidAmount.toFixed(2).replace('.', ',')}
              <span className="text-sm font-normal text-blue-600 ml-1">/ {auction.unit}</span>
            </div>
            <div className="text-xs text-blue-600 mt-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
              Sie wurden überboten! Geben Sie ein niedrigeres Gebot ab.
            </div>
          </div>
          {isAuctionActive() && (
            <button
              onClick={handleWithdrawBid}
              className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium"
            >
              <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
              Zurückziehen
            </button>
          )}
        </div>
      )}

      {/* Bid Form - Kompakt */}
      {isAuctionActive() ? (
        <div className="bg-white rounded-lg shadow-md p-5 mb-4">
          <h2 className="text-lg font-bold mb-3">
            {myCurrentBid ? 'Gebot anpassen' : 'Gebot abgeben'}
          </h2>

          <form onSubmit={handleSubmitBid} className="space-y-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Gebotspreis (€ pro {auction.unit}) *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500 font-semibold">€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={bidAmount}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value === '') {
                      setBidAmount('');
                      return;
                    }
                    value = value.replace(/[^0-9,.]/g, '');
                    setBidAmount(value);
                  }}
                  onBlur={() => {
                    if (bidAmount === '') return;
                    let value = bidAmount.replace(',', '.');
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      setBidAmount(numValue.toFixed(2).replace('.', ','));
                    }
                  }}
                  required
                  className="w-full pl-8 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg font-bold"
                  placeholder="0,00"
                />
              </div>
              {auction.currentLowestBid && (
                <p className="text-xs text-gray-600 mt-1">
                  Ihr Gebot muss niedriger sein als € {auction.currentLowestBid.toFixed(2).replace('.', ',')}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
            >
              {submitting ? (
                'Wird übermittelt...'
              ) : myCurrentBid ? (
                'Gebot aktualisieren'
              ) : (
                'Gebot abgeben'
              )}
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4 text-center">
          <FontAwesomeIcon icon={faClock} className="text-3xl text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 font-medium">
            {auction.status === 'ended' ? 'Diese Auktion ist beendet' : 'Gebotsabgabe noch nicht möglich'}
          </p>
        </div>
      )}

      {/* Bid History - Kompakt */}
      {myBids.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-5">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faHistory} />
            Ihre Gebots-Historie
          </h2>
          
          <div className="space-y-2">
            {myBids.map((bid) => (
              <div key={bid._id} className={`p-3 rounded-lg border ${
                bid.isCurrentLowest ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-bold text-base">€ {bid.bidAmount.toFixed(2).replace('.', ',')}</div>
                    <div className="text-xs text-gray-600">
                      {new Date(bid.submittedAt).toLocaleString('de-DE')}
                    </div>
                    {bid.modificationCount > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {bid.modificationCount}x geändert
                      </div>
                    )}
                  </div>
                  <div>
                    {bid.isCurrentLowest ? (
                      <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-medium">
                        <FontAwesomeIcon icon={faTrophy} /> Aktuell führend
                      </span>
                    ) : bid.status === 'outbid' ? (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                        Überboten
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium">
                        {bid.status}
                      </span>
                    )}
                  </div>
                </div>
                {bid.comment && (
                  <div className="mt-2 text-xs text-gray-700 italic">
                    "{bid.comment}"
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-2xl px-6 py-4 flex items-center gap-3 min-w-[300px] ${
            toast.type === 'success' ? 'bg-green-500 text-white' :
            toast.type === 'error' ? 'bg-red-500 text-white' :
            'bg-blue-500 text-white'
          }`}>
            <div className="text-2xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
            </div>
            <div className="flex-1 font-medium">{toast.message}</div>
            <button
              onClick={() => setToast(null)}
              className="text-white hover:text-gray-200 transition-colors ml-2"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierAuctionDetail;
